/**
 * Stock Candidates Widget — top stocks with unified scoring
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Skeleton, Tooltip, IconButton, alpha,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Refresh, Stars,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { marketEngineAPI } from '../../services/api';

const fmt = n => n >= 1e9 ? `${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}K` : n.toFixed(0);
const fmtPrice = p => new Intl.NumberFormat('vi-VN').format(Math.round(p));

const RATING_CLR = { EXCELLENT: '#10b981', GOOD: '#3b82f6', FAIR: '#f59e0b', WAIT: '#ef4444' };

const EmptyState = ({ t }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '50%', border: '2px dashed #e2e8f0', mx: 'auto', mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Stars sx={{ fontSize: 20, color: '#cbd5e1' }} />
    </Box>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8' }}>{t('tradingDecision.stockCandidates.empty', 'No candidates found')}</Typography>
  </Box>
);

const WidgetSkeleton = () => (
  <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5 }} />
        <Box><Skeleton width={140} height={16} /><Skeleton width={180} height={10} sx={{ mt: 0.4 }} /></Box>
      </Box>
      <Skeleton variant="circular" width={28} height={28} />
    </Box>
    {/* Header row */}
    <Box sx={{ display: 'grid', gridTemplateColumns: '40px 70px 1fr 60px 70px 60px 60px 60px 60px', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid #f1f5f9' }}>
      {Array(9).fill(0).map((_, i) => <Skeleton key={i} width="100%" height={12} />)}
    </Box>
    {/* Data rows */}
    {[0,1,2,3,4,5,6,7].map(r => (
      <Box key={r} sx={{ display: 'grid', gridTemplateColumns: '40px 70px 1fr 60px 70px 60px 60px 60px 60px', gap: 1.5, px: 2, py: 0.8 }}>
        {Array(9).fill(0).map((_, i) => <Skeleton key={i} width="100%" height={14} />)}
      </Box>
    ))}
  </Box>
);

const sectorLabel = (sector, lang) => {
  if (!sector) return '';
  if (typeof sector === 'object') return (lang === 'vi' ? sector.vi : sector.en) || sector.code || '';
  return sector;
};

const StockCandidatesWidget = ({ topK = 10 }) => {
  const { t, i18n } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = async () => {
    try {
      setLoading(true); setError(null);
      const r = await marketEngineAPI.getCandidates({ topk: topK, minLiquidity: 10000000000, minPrice: 10000 });
      if (r.data.success) { setCandidates(r.data.candidates || []); setLastUpdate(new Date()); }
      else throw new Error(r.data.error || 'Unknown');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [topK]); // eslint-disable-line

  if (loading) return <WidgetSkeleton />;

  if (error) {
    return (
      <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stars sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>{t('tradingDecision.stockCandidates.title', 'Top Stock Candidates')}</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
              {t('tradingDecision.stockCandidates.subtitle', 'Unified Scoring + Sector Boost')}
              {lastUpdate && ` · ${lastUpdate.toLocaleTimeString('vi-VN')}`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={load} size="small" disabled={loading} sx={{ width: 28, height: 28 }}>
          <Refresh sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {candidates.length === 0 ? <EmptyState t={t} /> : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    { key: 'rank', align: 'left' },
                    { key: 'ticker', align: 'left' },
                    { key: 'sector', align: 'left' },
                    { key: 'score', align: 'right' },
                    { key: 'entry', align: 'right' },
                    { key: 'rating', align: 'left' },
                    { key: 'stop', align: 'right' },
                    { key: 'riskPct', align: 'right' },
                    { key: 'size', align: 'right' },
                  ].map(h => (
                    <TableCell key={h.key} align={h.align}
                      sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>
                      {t(`tradingDecision.stockCandidates.columns.${h.key}`)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((c, idx) => {
                  const ratingColor = RATING_CLR[c.entry_zone?.rating] || '#94a3b8';
                  return (
                    <TableRow key={c.ticker} hover sx={{ '&:hover': { bgcolor: alpha('#667eea', 0.02) } }}>
                      <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.72rem', color: '#94a3b8', borderColor: '#f8fafc' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', borderColor: '#f8fafc' }}>{c.ticker}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{sectorLabel(c.sector, i18n.language)}</Typography>
                          {c.sector_boost > 0.10 && <TrendingUp sx={{ fontSize: 12, color: '#10b981' }} />}
                          {c.sector_boost < -0.05 && <TrendingDown sx={{ fontSize: 12, color: '#ef4444' }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
                        <Tooltip title={t('tradingDecision.stockCandidates.tooltips.scoreBreakdown', { base: c.base_score.toFixed(2), boost: (c.sector_boost * 100).toFixed(1) })}>
                          <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{c.score.toFixed(1)}</Typography>
                            {Math.abs(c.sector_boost) > 0.05 && (
                              <Typography sx={{ fontSize: '0.6rem', color: c.sector_boost > 0 ? '#10b981' : '#ef4444' }}>
                                {c.sector_boost > 0 ? '+' : ''}{(c.sector_boost * 100).toFixed(0)}%
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.78rem', fontWeight: 500, borderColor: '#f8fafc' }}>
                        <Tooltip title={t('tradingDecision.stockCandidates.tooltips.entryZone', { min: fmtPrice(c.entry_zone.min), max: fmtPrice(c.entry_zone.max), best: fmtPrice(c.entry_zone.best) })}>
                          <span>{fmtPrice(c.entry)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ratingColor }}>
                          {t(`tradingDecision.stockCandidates.rating.${(c.entry_zone?.rating || '').toLowerCase()}`, c.entry_zone?.rating)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.78rem', color: '#ef4444', borderColor: '#f8fafc' }}>{fmtPrice(c.stop)}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: c.risk_pct < 3 ? '#10b981' : c.risk_pct < 7 ? '#f59e0b' : '#ef4444' }}>
                          {c.risk_pct.toFixed(2)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.72rem', color: '#64748b', borderColor: '#f8fafc' }}>{fmt(c.position_value)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ px: 2, py: 0.8, borderTop: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>
              {t('tradingDecision.stockCandidates.legend')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default StockCandidatesWidget;
